import { formatDate } from 'date-fns/format';
import { useEffect, useState } from 'react';
import { Configuration, OrdersApi } from '@/api';
import Checkbox from '@/components/Checkbox';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import Pagination from '@/components/Pagination';
import { SearchProps } from '@/components/search/useSearch';
import useCheckboxes from '@/hooks/useCheckboxes';
import { EmptyResult } from '@/models/Pagination';
import { TimeWindow } from '@/models/TimeWindow';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { Order, OrderAssociation, Orders } from '@/modules/order/models/Order';
import {
  getCustomerOrderStatusTitle,
  getSupplierOrderStatusTitle,
  isOrderAccepted,
} from '@/modules/order/models/OrderStatus';
import { OrderDetailsPath } from '@/modules/order/paths';
import { arrayEqual } from '@/utils/array';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

interface OrderTableProps extends SearchProps {
  storeId?: string | null;
  supplierId?: string | null;
  status?: string | null;
  period?: TimeWindow | null;
  isSupplier?: boolean;
  onChangeCheckedItems?: (items: string[]) => void;
}

const initialState = EmptyResult<Order>();

const OrderTable = ({
  searchQuery,
  page = 1,
  onChangePage = () => {},
  storeId,
  supplierId,
  status,
  period,
  isSupplier = false,
  onChangeCheckedItems = () => {},
}: OrderTableProps) => {
  const { checkboxes, handleMasterToggle, handleRowToggle, areAllChecked, updateCheckboxes } =
    useCheckboxes({
      onChange: onChangeCheckedItems,
    });

  const [orders, setOrders] = useState<Orders>(initialState);

  const ordersApi = new OrdersApi(new Configuration(useAuth));

  const { isLoading, data, isFetching } = useQuery({
    queryKey: ['orders', page, searchQuery, storeId, supplierId, status, period],
    queryFn: async () => {
      const limit = 10;
      return ordersApi.findOrders(
        searchQuery,
        {
          storeId: storeId ?? undefined,
          supplierId: supplierId ?? undefined,
          status: status ?? undefined,
          period: period ?? undefined,
        },
        page * limit - limit,
        limit,
        [OrderAssociation.SUPPLIER, OrderAssociation.STORE],
      );
    },
  });

  useEffect(() => {
    if (!isLoading && !isFetching) {
      updateCheckboxes(data?.items.map((item) => item.id) || []);
    }

    setOrders((prevState): Orders => {
      if (isLoading) {
        return prevState;
      }

      if (
        (prevState.items.length === 0 && !data) ||
        (data?.items && arrayEqual(data.items, prevState.items))
      ) {
        return prevState;
      }

      return {
        items: data?.items || [],
        totalCount: data?.totalCount || 0,
        estimatedTotalCount: data?.estimatedTotalCount || 0,
      };
    });
  }, [data, isLoading, isFetching, updateCheckboxes]);

  return (
    <div className="table-container" data-testid="order-list">
      <div className="table-wrapper">
        <table className={isLoading ? 'loading' : ''} data-testid="order-list-table">
          <thead>
            <tr>
              <th className="cell-checkbox min-w-14">
                <Checkbox
                  data-testid="order-list-select-all"
                  checked={!isLoading && areAllChecked(data?.items)}
                  onChange={handleMasterToggle}
                />
              </th>
              <th className="">Order ID</th>
              {isSupplier ? (
                <th className="min-w-40">Retailer</th>
              ) : (
                <th className="min-w-40">Supplier</th>
              )}
              <th className="">Created At</th>
              {!isSupplier && <th className="min-w-40">Scheduled Delivery Date</th>}
              <th className="">Status</th>
            </tr>
          </thead>
          <tbody>
            {orders.items.map((order, index) => {
              return (
                <tr
                  key={index}
                  data-testid="order-row"
                  data-order-id={order.id}
                  data-order-code={order.code}
                >
                  <td className="cell-checkbox">
                    <Checkbox
                      data-testid="order-row-select"
                      checked={checkboxes.checked(order.id)}
                      onChange={() => handleRowToggle(order.id)}
                    />
                  </td>
                  <td>
                    <Link
                      to={OrderDetailsPath.withId(order.id)}
                      className="text-blue-500 hover:underline"
                      data-testid="order-row-link"
                    >
                      {order.code}
                    </Link>
                  </td>
                  {isSupplier ? (
                    <td>{order.store ? order.store.name : ''}</td>
                  ) : (
                    <td>{order.supplier ? order.supplier.name : ''}</td>
                  )}
                  <td>{formatDate(order.createdAt, 'd MMM yyyy')}</td>
                  {!isSupplier && (
                    <td>
                      {order.deliveryWindow?.start && order.deliveryWindow?.end && (
                        <>
                          {formatDate(order.deliveryWindow.start, 'EEEE, d MMM yyyy')}
                          ,<br />
                          {formatDate(order.deliveryWindow.start, 'h:mm a')} -{' '}
                          {formatDate(order.deliveryWindow.end, 'h:mm a')}
                        </>
                      )}
                    </td>
                  )}
                  <td>
                    <div className="flex flex-col items-start gap-1.5">
                      <span className={isOrderAccepted(order) ? 'text-green-700' : ''}>
                        {isSupplier
                          ? getSupplierOrderStatusTitle(order)
                          : getCustomerOrderStatusTitle(order)}
                      </span>
                      {isSupplier && order.unresolvedIncompleteDelivery && (
                        <span
                          className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800"
                          data-testid="order-row-incomplete-delivery-badge"
                        >
                          <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
                          Incomplete report
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {isLoading ? (
          <div role="status" className="loader">
            <SpinnerIcon />
            <span className="sr-only">Loading...</span>
          </div>
        ) : (
          ''
        )}
      </div>
      <div className="table-bottom">
        <Pagination
          currentPage={page}
          itemsPerPage={10}
          totalCount={orders.totalCount || 0}
          estimatedTotalCount={orders.estimatedTotalCount || 0}
          onClick={onChangePage}
        />
      </div>
    </div>
  );
};

export default OrderTable;
